import { defineComponent as _defineComponent } from 'vue';
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex" };
const _hoisted_2 = { class: "flex flex-col gap-2 min-w-[200px] py-4 pl-[0.663rem] pr-[0.663rem] border-r border-surface-100" };
const _hoisted_3 = ["onClick"];
const _hoisted_4 = { class: "flex flex-col gap-5 p-4" };
const _hoisted_5 = { class: "flex justify-end items-center gap-2 border-t border-surface-100 py-2" };
import Popover from "primevue/popover";
import { DatePicker as GlobalDatePicker } from "@/utils/globalPassThrough";
import DatePicker from "primevue/datepicker";
import { INTERVAL } from "@/utils/const";
import { ref, computed, watch, onUnmounted, nextTick } from "vue";
import { generateDailyRangeDates, removeNotFullWeeks, appendNotFullWeeks, compareDateRanges, PREDEFINED_DATE_RANGES, DEFAULT_DATE_RANGE, } from "@/utils/util";
import { CUSTOM_DATE_RANGES_LABELS } from "@/utils/const";
import { ButtonPrimary, ButtonSecondary } from "@/utils/globalPassThrough";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DatePickerPopover',
    props: {
        modelValue: { default: null },
        customLabel: { default: "" },
        selectedInterval: { default: INTERVAL.DAILY }
    },
    emits: ["update:modelValue", "update:customLabel"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // region Predefined date ranges
        const defaultDateRanges = computed(() => {
            return {
                label: CUSTOM_DATE_RANGES_LABELS.DEFAULT,
                value: {
                    [INTERVAL.DAILY]: {
                        from: DEFAULT_DATE_RANGE[INTERVAL.DAILY].from,
                        to: DEFAULT_DATE_RANGE[INTERVAL.DAILY].to,
                    },
                    [INTERVAL.WEEKLY]: {
                        from: DEFAULT_DATE_RANGE[INTERVAL.WEEKLY].from,
                        to: DEFAULT_DATE_RANGE[INTERVAL.WEEKLY].to,
                    },
                },
            };
        });
        const predefinedDateRanges = computed(() => {
            return [defaultDateRanges.value, ...Object.values(PREDEFINED_DATE_RANGES)];
        });
        const selectPredefinedRange = (from, to, label) => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                draftModelValue.value = generateDailyRangeDates(from, to);
            }
            else {
                draftModelValue.value = [from, to];
            }
            draftCustomLabel.value = label;
        };
        // endregion
        // region Draft logic
        const draftModelValue = ref(props.modelValue);
        const draftCustomLabel = ref(props.customLabel);
        const updateDraftModelValue = (value) => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                const appendedDays = appendNotFullWeeks(value);
                draftModelValue.value = removeNotFullWeeks(appendedDays);
                draftCustomLabel.value = "";
            }
            else {
                draftModelValue.value = value;
                draftCustomLabel.value = "";
            }
        };
        const resetAll = () => {
            draftModelValue.value = props.modelValue;
            draftCustomLabel.value = props.customLabel;
        };
        const apply = () => {
            emit("update:modelValue", draftModelValue.value);
            emit("update:customLabel", draftCustomLabel.value);
            datePickerPopoverRef.value?.toggle();
        };
        // endregion
        // region Popover ref
        const datePickerPopoverRef = ref(null);
        watch(() => datePickerPopoverRef.value?.visible, () => {
            resetAll();
        });
        __expose({
            datePickerPopoverRef,
        });
        // endregion
        // region Disabled logic
        const isClearAllDisabled = computed(() => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                return compareDateRanges(props.modelValue, draftModelValue.value);
            }
            else {
                const firstDate = draftModelValue.value[0];
                const secondDate = draftModelValue.value[1];
                return (firstDate !== props.modelValue[0] || secondDate !== props.modelValue[1]);
            }
        });
        const isApplyDisabled = computed(() => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                return (!Array.isArray(draftModelValue.value) ||
                    draftModelValue.value.length === 0);
            }
            else {
                return draftModelValue.value[1] === null;
            }
        });
        // endregion
        // region Week hover logic (only for weekly view)
        const setupWeekHover = () => {
            if (props.selectedInterval !== INTERVAL.WEEKLY)
                return;
            nextTick(() => {
                const weeklyPicker = document.querySelector(".ffy-weekly");
                if (!weeklyPicker)
                    return;
                const rows = weeklyPicker.querySelectorAll("tr");
                rows.forEach((row) => {
                    row.addEventListener("mouseenter", handleRowMouseEnter);
                    row.addEventListener("mouseleave", handleRowMouseLeave);
                });
            });
        };
        const cleanupWeekHover = () => {
            const weeklyPicker = document.querySelector(".ffy-weekly");
            if (!weeklyPicker)
                return;
            const rows = weeklyPicker.querySelectorAll("tr");
            rows.forEach((row) => {
                row.removeEventListener("mouseenter", handleRowMouseEnter);
                row.removeEventListener("mouseleave", handleRowMouseLeave);
                row.classList.remove("ffy-week-hover");
            });
        };
        const handleRowMouseEnter = (e) => {
            const row = e.currentTarget;
            if (!(row instanceof HTMLElement))
                return;
            // Only add hover if the row contains day cells (not header row)
            if (row.querySelector(".p-datepicker-day-cell")) {
                row.classList.add("ffy-week-hover");
            }
        };
        const handleRowMouseLeave = (e) => {
            const row = e.currentTarget;
            if (!(row instanceof HTMLElement))
                return;
            row.classList.remove("ffy-week-hover");
        };
        const handleMonthChange = () => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                cleanupWeekHover();
                setTimeout(setupWeekHover, 100);
            }
        };
        const handleYearChange = () => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                cleanupWeekHover();
                setTimeout(setupWeekHover, 100);
            }
        };
        // Setup hover when popover becomes visible
        watch(() => datePickerPopoverRef.value?.visible, (visible) => {
            if (visible && props.selectedInterval === INTERVAL.WEEKLY) {
                // Small delay to ensure DOM is ready
                setTimeout(setupWeekHover, 100);
            }
            else {
                cleanupWeekHover();
            }
        });
        // Re-setup when interval changes
        watch(() => props.selectedInterval, (interval) => {
            cleanupWeekHover();
            if (interval === INTERVAL.WEEKLY && datePickerPopoverRef.value?.visible) {
                setTimeout(setupWeekHover, 100);
            }
        });
        onUnmounted(() => {
            cleanupWeekHover();
        });
        // endregion
        return (_ctx, _cache) => {
            const _component_Button = _resolveComponent("Button");
            return (_openBlock(), _createBlock(_unref(Popover), {
                ref_key: "datePickerPopoverRef",
                ref: datePickerPopoverRef,
                pt: {
                    root: {
                        class: '!bg-surface-0 !p-0 !rounded-xl dark:!border-surface-100',
                    },
                }
            }, {
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("div", _hoisted_2, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(predefinedDateRanges.value, (range) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: range.label,
                                    class: _normalizeClass(['flex flex-col gap-2']),
                                    onClick: ($event) => (selectPredefinedRange(range.value[__props.selectedInterval].from, range.value[__props.selectedInterval].to, range.label))
                                }, [
                                    _createElementVNode("span", {
                                        class: _normalizeClass([
                                            'text-surface-700 text-sm py-[0.438rem] px-[0.663rem] cursor-pointer',
                                            {
                                                'bg-surface-100 !text-primary-500 rounded-md w-fit': draftCustomLabel.value === range.label,
                                            },
                                        ])
                                    }, _toDisplayString(_ctx.$tf(range.label)), 3)
                                ], 8, _hoisted_3));
                            }), 128))
                        ]),
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_unref(DatePicker), {
                                modelValue: draftModelValue.value,
                                "onUpdate:modelValue": updateDraftModelValue,
                                inline: "",
                                fluid: "",
                                "number-of-months": 2,
                                "selection-mode": __props.selectedInterval === _unref(INTERVAL).DAILY ? 'range' : 'multiple',
                                onMonthChange: handleMonthChange,
                                onYearChange: handleYearChange,
                                pt: {
                                    ..._unref(GlobalDatePicker),
                                    root: {
                                        class: [
                                            '!h-content',
                                            { 'ffy-weekly': __props.selectedInterval === _unref(INTERVAL).WEEKLY },
                                            { 'ffy-daily': __props.selectedInterval === _unref(INTERVAL).DAILY },
                                        ],
                                    },
                                    pcPrevButton: {
                                        root: {
                                            class: '!bg-surface-100 !p-2.5 !rounded-md !text-surface-800 !cursor-pointer !h-[2rem] !w-[2rem]',
                                        },
                                    },
                                    pcNextButton: {
                                        root: {
                                            class: '!bg-surface-100 !p-2.5 !rounded-md !text-surface-800 !cursor-pointer !h-[2rem] !w-[2rem]',
                                        },
                                    },
                                    calendar: {
                                        class: '!border-0',
                                    },
                                    header: {
                                        class: '!border-0 !bg-surface-0 !justify-start !gap-5 !pb-0 !mb-10 !w-[250px]',
                                    },
                                    panel: {
                                        class: '!border-0 !bg-surface-0 !p-0',
                                    },
                                    daycell: {
                                        class: '!p-0',
                                    },
                                    day: {
                                        class: '!border-0 !w-auto !p-1 !mt-2 dark:!text-surface-800 hover:dark:!bg-primary-100',
                                    },
                                    selectmonth: {
                                        class: '!text-surface-900 !font-semibold !text-xl',
                                    },
                                    selectyear: {
                                        class: '!text-surface-900 !font-semibold !text-xl',
                                    },
                                }
                            }, null, 8, ["modelValue", "selection-mode", "pt"]),
                            _createElementVNode("div", _hoisted_5, [
                                _createVNode(_component_Button, {
                                    label: _ctx.$tf('datepickerPopover.clearAll'),
                                    onClick: resetAll,
                                    pt: _unref(ButtonSecondary),
                                    disabled: isClearAllDisabled.value
                                }, null, 8, ["label", "pt", "disabled"]),
                                _createVNode(_component_Button, {
                                    label: _ctx.$tf('datepickerPopover.apply'),
                                    onClick: apply,
                                    pt: _unref(ButtonPrimary),
                                    disabled: isApplyDisabled.value
                                }, null, 8, ["label", "pt", "disabled"])
                            ])
                        ])
                    ])
                ]),
                _: 1
            }, 512));
        };
    }
});
